import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Calendar, Clock, MapPin, Users, MessageSquare, Send, Mail, Phone,
  Bell, AlertCircle, Loader2, ChevronRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import ManagerChatDrawer from '@/components/manager/ManagerChatDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MANAGER COMMAND CENTER
 * ----------------------
 * Operational dashboard tailored for Team Managers. Surfaces what they
 * actually need: this week's events, pending RSVPs, quick announcement
 * composer, parent contact tiles, and a 1:1 chat drawer.
 */
const ManagerDashboard = () => {
  const { currentTeam, user } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Announcement composer
  const [annTitle, setAnnTitle] = useState('');
  const [annBody, setAnnBody] = useState('');
  const [annSending, setAnnSending] = useState(false);

  // Chat drawer
  const [chatOpen, setChatOpen] = useState(false);
  const [chatParent, setChatParent] = useState(null);

  useEffect(() => {
    if (currentTeam) fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('team_token');
      const res = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/manager-dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setData(await res.json());
    } catch (e) {
      // graceful — dashboard is read-only
    } finally {
      setLoading(false);
    }
  };

  const sendAnnouncement = async () => {
    const title = annTitle.trim();
    const message = annBody.trim();
    if (!title || !message) {
      toast.error('Title and message are required');
      return;
    }
    setAnnSending(true);
    try {
      const token = localStorage.getItem('team_token');
      const res = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/messages`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, message, message_type: 'announcement' }),
        }
      );
      if (res.ok) {
        toast.success('Announcement sent to the team');
        setAnnTitle('');
        setAnnBody('');
        fetchDashboard();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send');
      }
    } finally {
      setAnnSending(false);
    }
  };

  const waLink = (phone) => {
    if (!phone) return null;
    const digits = String(phone).replace(/\D/g, '');
    return digits.length >= 8 ? `https://wa.me/${digits}` : null;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = parseISO(iso);
      if (isToday(d)) return `Today • ${format(d, 'HH:mm')}`;
      if (isTomorrow(d)) return `Tomorrow • ${format(d, 'HH:mm')}`;
      return format(d, 'EEE, MMM d • HH:mm');
    } catch (e) {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-muted-foreground" size={32} />
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="mx-auto mb-3 text-muted-foreground" size={32} />
          <p className="text-muted-foreground">Couldn't load the command center.</p>
        </CardContent>
      </Card>
    );
  }

  const { stats, upcoming_sessions, recent_announcements, quick_contacts } = data;

  return (
    <div className="space-y-6" data-testid="manager-dashboard">
      {/* Hero */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={14} className="text-primary" />
          <span className="text-xs uppercase tracking-widest text-primary">Command Center</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-heading">
          Hi {(user?.full_name || 'Manager').split(' ')[0]}.
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Here's what's happening with {currentTeam?.team_name} this week.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={Users} label="Roster" value={stats.roster_size} testid="stat-roster" />
        <StatTile icon={Calendar} label="This Week" value={stats.upcoming_count} testid="stat-week" />
        <StatTile
          icon={AlertCircle}
          label="Pending RSVPs"
          value={stats.pending_rsvps}
          accent={stats.pending_rsvps > 0 ? 'amber' : null}
          testid="stat-rsvps"
        />
        <StatTile
          icon={MessageSquare}
          label="Unread Chats"
          value={stats.unread_chat || 0}
          accent={(stats.unread_chat || 0) > 0 ? 'primary' : null}
          testid="stat-unread"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT — Upcoming + Announcement composer */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upcoming Sessions */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-medium flex items-center gap-2">
                  <Calendar size={16} className="text-primary" /> This Week
                </h2>
                <span className="text-xs text-muted-foreground">{upcoming_sessions.length} events</span>
              </div>
              {upcoming_sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No sessions scheduled for the next 7 days.
                </p>
              ) : (
                <div className="space-y-2">
                  {upcoming_sessions.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between p-3 rounded-sm bg-muted/40 border border-border/40 hover:border-primary/40 transition"
                      data-testid={`upcoming-${s.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-xs mb-1">
                          <span
                            className={`px-2 py-0.5 rounded uppercase tracking-wider text-[10px] ${
                              s.session_type === 'game'
                                ? 'bg-amber-500/15 text-amber-400'
                                : s.session_type === 'tournament'
                                ? 'bg-purple-500/15 text-purple-400'
                                : 'bg-emerald-500/15 text-emerald-400'
                            }`}
                          >
                            {s.session_type || 'training'}
                          </span>
                          <span className="text-muted-foreground">{formatDate(`${s.date}T${s.start_time || '00:00'}`)}</span>
                        </div>
                        <p className="font-medium truncate">{s.title || `${s.session_type || 'Training'} session`}</p>
                        {s.location && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin size={11} /> {s.location}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs ml-3 shrink-0">
                        <div className="text-emerald-400 font-medium">
                          {s.attendance_counts?.confirmed || 0} ✓
                        </div>
                        {s.attendance_counts?.pending > 0 && (
                          <div className="text-amber-400">
                            {s.attendance_counts.pending} pending
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Announcement composer */}
          <Card data-testid="announcement-composer">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Bell size={16} className="text-primary" />
                <h2 className="font-medium">Send Announcement</h2>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Broadcasts to every parent in {currentTeam?.team_name}. They'll see it on their dashboard.
              </p>
              <div className="space-y-2">
                <Input
                  value={annTitle}
                  onChange={(e) => setAnnTitle(e.target.value)}
                  placeholder="Title (e.g. Saturday's game canceled)"
                  maxLength={120}
                  data-testid="announcement-title"
                />
                <Textarea
                  value={annBody}
                  onChange={(e) => setAnnBody(e.target.value)}
                  placeholder="What do parents need to know?"
                  rows={3}
                  maxLength={2000}
                  data-testid="announcement-body"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">
                    {annBody.length}/2000
                  </span>
                  <Button
                    onClick={sendAnnouncement}
                    disabled={annSending || !annTitle.trim() || !annBody.trim()}
                    className="bg-primary text-black hover:bg-primary/90"
                    data-testid="announcement-send"
                  >
                    {annSending ? (
                      <><Loader2 className="animate-spin mr-2" size={14} /> Sending</>
                    ) : (
                      <><Send size={14} className="mr-2" /> Send to {stats.roster_size} families</>
                    )}
                  </Button>
                </div>
              </div>

              {recent_announcements.length > 0 && (
                <div className="mt-5 pt-4 border-t border-border/40">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    Recent
                  </p>
                  <div className="space-y-2">
                    {recent_announcements.slice(0, 3).map((a) => (
                      <div key={a.id} className="text-xs" data-testid={`recent-ann-${a.id}`}>
                        <p className="font-medium truncate">{a.title}</p>
                        <p className="text-muted-foreground line-clamp-1">{a.message}</p>
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                          {a.created_at ? format(parseISO(a.created_at), 'MMM d, HH:mm') : ''} • {a.sent_by_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — Quick contacts */}
        <div className="space-y-6">
          <Card data-testid="quick-contacts">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users size={16} className="text-primary" />
                <h2 className="font-medium">Quick Contacts</h2>
              </div>
              {quick_contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No parent contacts yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {quick_contacts.map((c) => {
                    const wa = waLink(c.parent_phone);
                    const unread = c.unread_count || 0;
                    return (
                      <div
                        key={c.athlete_id}
                        className={`border rounded-sm p-3 transition ${
                          unread > 0
                            ? 'border-primary/60 bg-primary/5'
                            : 'border-border/40 hover:border-primary/40'
                        }`}
                        data-testid={`contact-${c.athlete_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="min-w-0 flex items-center gap-2">
                            <div className="relative shrink-0">
                              <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">
                                {(c.parent_name || '?').charAt(0).toUpperCase()}
                              </div>
                              {unread > 0 && (
                                <span
                                  className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                                  data-testid={`contact-unread-${c.athlete_id}`}
                                >
                                  {unread > 9 ? '9+' : unread}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{c.parent_name || 'Parent'}</p>
                              <p className="text-[11px] text-muted-foreground truncate">
                                Parent of {c.athlete_name}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {c.parent_email && (
                            <a
                              href={`mailto:${c.parent_email}`}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-muted/60 hover:bg-primary/10 hover:text-primary text-[11px] transition"
                              title={c.parent_email}
                              data-testid={`contact-email-${c.athlete_id}`}
                            >
                              <Mail size={11} /> Email
                            </a>
                          )}
                          {wa && (
                            <a
                              href={wa}
                              target="_blank"
                              rel="noreferrer"
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-muted/60 hover:bg-emerald-500/10 hover:text-emerald-400 text-[11px] transition"
                              title={c.parent_phone}
                              data-testid={`contact-wa-${c.athlete_id}`}
                            >
                              <Phone size={11} /> WhatsApp
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setChatParent({ id: c.parent_id, name: c.parent_name, email: c.parent_email });
                              setChatOpen(true);
                            }}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-muted/60 hover:bg-primary/10 hover:text-primary text-[11px] transition"
                            data-testid={`contact-chat-${c.athlete_id}`}
                          >
                            <MessageSquare size={11} /> Chat
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button
                variant="ghost"
                onClick={() => window.location.assign(`/team/${currentTeam?.team_id}/athletes`)}
                className="w-full mt-3 text-xs"
                data-testid="see-full-roster"
              >
                See full roster <ChevronRight size={12} />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Chat drawer */}
      <ManagerChatDrawer
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
          fetchDashboard(); // refresh unread count
        }}
        teamId={currentTeam?.team_id}
        parent={chatParent}
      />
    </div>
  );
};

const StatTile = ({ icon: Icon, label, value, accent, testid }) => {
  const accentClass =
    accent === 'amber' ? 'text-amber-400' : accent === 'primary' ? 'text-primary' : 'text-foreground';
  return (
    <div
      className="bg-card border border-border/40 rounded-sm p-3 flex items-center gap-3"
      data-testid={testid}
    >
      <Icon size={18} className="text-muted-foreground" />
      <div>
        <p className={`text-2xl font-bold leading-tight ${accentClass}`}>{value ?? 0}</p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      </div>
    </div>
  );
};

export default ManagerDashboard;
